import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  consumeInstagramOAuthReturnResult,
  peekInstagramOAuthReturnResult,
} from '../utils/instagramOAuth';

const pathsMatch = (expectedPath, currentPath) => {
  if (!expectedPath) {
    return true;
  }

  return expectedPath === currentPath;
};

/**
 * Handles the return leg of a same-tab Instagram OAuth redirect on mount.
 */
export const useInstagramOAuthReturn = ({
  onSuccess,
  onFailure,
  enabled = true,
} = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const callbacksRef = useRef({ onSuccess, onFailure });

  callbacksRef.current = { onSuccess, onFailure };

  useEffect(() => {
    if (!enabled || handledRef.current) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}`;
    const stateResult = location.state?.instagramOAuth;
    const storedResult = stateResult ? null : peekInstagramOAuthReturnResult();
    const result = stateResult || storedResult;

    if (!result) {
      return;
    }

    if (!pathsMatch(result.returnPath, currentPath)) {
      return;
    }

    handledRef.current = true;
    consumeInstagramOAuthReturnResult();

    if (location.state?.instagramOAuth) {
      navigate(currentPath, { replace: true, state: null });
    }

    if (result.success) {
      callbacksRef.current.onSuccess?.(result);
      return;
    }

    callbacksRef.current.onFailure?.(result);
  }, [enabled, location.pathname, location.search, location.state, navigate]);
};
